import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Custom event for selling plan changes
 * @extends {Event}
 */
export class SellingPlanChangeEvent extends Event {
  /**
   * Creates a new SellingPlanChangeEvent
   * @param {string|null} sellingPlanId - The selected selling plan ID, or null for one-time purchase
   * @param {number|null} price - The price for the selected plan
   */
  constructor(sellingPlanId, price) {
    super(SellingPlanChangeEvent.eventName, { bubbles: true });
    this.detail = {
      sellingPlanId,
      price,
    };
  }

  static eventName = 'selling-plan:change';
}

/**
 * A custom element that manages subscription/one-time purchase selection.
 *
 * @typedef {object} SubscriptionWidgetRefs
 * @property {HTMLInputElement} subscriptionRadio - The subscription radio input.
 * @property {HTMLInputElement} onetimeRadio - The one-time purchase radio input.
 * @property {HTMLSelectElement} sellingPlanSelect - The selling plan dropdown.
 * @property {HTMLInputElement} sellingPlanInput - The hidden input for selling plan ID.
 * @property {HTMLElement} subscriptionOption - The subscription option label container.
 * @property {HTMLElement} onetimeOption - The one-time option label container.
 * @property {HTMLElement} benefitsContainer - The benefits container element.
 * @property {HTMLElement} subscriptionPrice - The subscription price display element.
 * @property {HTMLElement} onetimePrice - The one-time price display element.
 * @property {HTMLScriptElement} productData - The script element containing product JSON data.
 * @property {HTMLButtonElement} learnMoreButton - The learn more button that opens the modal.
 * @property {HTMLElement} modal - The subscription benefits modal.
 * @property {HTMLElement} modalBackdrop - The modal backdrop.
 * @property {HTMLButtonElement} modalCloseButton - The modal close button.
 * @property {HTMLButtonElement} modalGotItButton - The modal "Got it" button.
 *
 * @extends Component<SubscriptionWidgetRefs>
 */
class SubscriptionWidget extends Component {
  requiredRefs = ['subscriptionRadio', 'onetimeRadio', 'sellingPlanSelect', 'sellingPlanInput'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {Object} */
  #productData = null;

  /** @type {Object.<string, Object>} */
  #variantAllocationsMap = {};

  /** @type {HTMLInputElement|null} */
  #formSellingPlanInput = null;

  connectedCallback() {
    super.connectedCallback();

    // Parse product data
    if (this.refs.productData) {
      try {
        this.#productData = JSON.parse(this.refs.productData.textContent);
        this.#buildAllocationsMap();
      } catch (e) {
        console.error('Failed to parse subscription widget product data:', e);
      }
    }

    // Listen for variant changes
    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Find the product form and inject selling plan input
    this.#injectSellingPlanInput();

    // Initialize state - default to one-time purchase
    this.#updateSellingPlanInput();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    // Clean up injected input
    if (this.#formSellingPlanInput) {
      this.#formSellingPlanInput.remove();
      this.#formSellingPlanInput = null;
    }

    // Clean up modal event listener
    document.removeEventListener('keydown', this.#handleEscapeKey);
    document.body.style.overflow = '';
  }

  /**
   * Finds the product form and injects a selling plan input into it
   */
  #injectSellingPlanInput() {
    // Find the product form in the same section
    const section = this.closest('.shopify-section, dialog');
    const form = section?.querySelector('form[data-type="add-to-cart-form"]');

    if (!form) {
      console.warn('Subscription widget: Could not find product form');
      return;
    }

    // Check if there's already a selling plan input
    let existingInput = form.querySelector('input[name="selling_plan"]');

    if (!existingInput) {
      // Create and inject the selling plan input
      existingInput = document.createElement('input');
      existingInput.type = 'hidden';
      existingInput.name = 'selling_plan';
      existingInput.value = '';
      form.appendChild(existingInput);
    }

    this.#formSellingPlanInput = /** @type {HTMLInputElement} */ (existingInput);
  }

  /**
   * Builds a map of variant allocations for quick lookup
   */
  #buildAllocationsMap() {
    if (!this.#productData?.variantAllocations) return;

    for (const allocation of this.#productData.variantAllocations) {
      this.#variantAllocationsMap[allocation.selling_plan_id] = allocation;
    }
  }

  /**
   * Opens the subscription benefits modal
   */
  openModal() {
    if (this.refs.modal) {
      this.refs.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      // Add class to parent section for z-index on mobile
      const parentSection = this.closest('.shopify-section');
      parentSection?.classList.add('subscription-modal-open');

      // Focus the close button for accessibility
      this.refs.modalCloseButton?.focus();
      // Add escape key listener
      document.addEventListener('keydown', this.#handleEscapeKey);
    }
  }

  /**
   * Closes the subscription benefits modal
   */
  closeModal() {
    if (this.refs.modal) {
      this.refs.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';

      // Remove class from parent section
      const parentSection = this.closest('.shopify-section');
      parentSection?.classList.remove('subscription-modal-open');

      // Remove escape key listener
      document.removeEventListener('keydown', this.#handleEscapeKey);
      // Return focus to learn more button
      this.refs.learnMoreButton?.focus();
    }
  }

  /**
   * Handles escape key press to close modal
   * @param {KeyboardEvent} event
   */
  #handleEscapeKey = (event) => {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  };

  /**
   * Handles purchase type change (subscription vs one-time)
   * @param {Event} event - The change event
   */
  handlePurchaseTypeChange(event) {
    const value = /** @type {HTMLInputElement} */ (event.target).value;
    this.#updateSellingPlanInput();

    // Dispatch event for other components to react
    const sellingPlanId = value === 'subscription' ? this.refs.sellingPlanSelect.value : null;
    const price = this.#getCurrentPrice(sellingPlanId);
    this.dispatchEvent(new SellingPlanChangeEvent(sellingPlanId, price));
  }

  /**
   * Handles selling plan dropdown change
   * @param {Event} event - The change event
   */
  handleSellingPlanChange(event) {
    this.#updateSellingPlanInput();

    // Update displayed price
    const selectedOption = /** @type {HTMLSelectElement} */ (event.target).selectedOptions[0];
    if (selectedOption && this.refs.subscriptionPrice) {
      const price = selectedOption.dataset.price;
      if (price) {
        this.refs.subscriptionPrice.textContent = this.#formatMoney(parseInt(price, 10));
      }
    }

    // Dispatch event
    const sellingPlanId = this.refs.sellingPlanSelect.value;
    const price = this.#getCurrentPrice(sellingPlanId);
    this.dispatchEvent(new SellingPlanChangeEvent(sellingPlanId, price));
  }

  /**
   * Updates the hidden selling plan input based on current selection
   */
  #updateSellingPlanInput() {
    const isSubscription = this.refs.subscriptionRadio.checked;
    const sellingPlanValue = isSubscription ? this.refs.sellingPlanSelect.value : '';

    // Update local ref input
    this.refs.sellingPlanInput.value = sellingPlanValue;
    this.refs.sellingPlanInput.disabled = !isSubscription;

    // Update the form input
    if (this.#formSellingPlanInput) {
      this.#formSellingPlanInput.value = sellingPlanValue;
    }
  }

  /**
   * Gets the current price based on selling plan selection
   * @param {string|null} sellingPlanId - The selling plan ID
   * @returns {number|null} The price in cents
   */
  #getCurrentPrice(sellingPlanId) {
    if (!sellingPlanId) return null;

    const allocation = this.#variantAllocationsMap[sellingPlanId];
    return allocation?.price ?? null;
  }

  /**
   * Formats money in cents to a display string
   * @param {number} cents - The amount in cents
   * @returns {string} Formatted money string
   */
  #formatMoney(cents) {
    return Shopify.formatMoney(cents, Theme.moneyFormat);
  }

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail.resource;
    if (!variant) return;

    // Update product data with new variant allocations
    if (variant.selling_plan_allocations) {
      this.#productData.variantAllocations = variant.selling_plan_allocations;
      this.#variantAllocationsMap = {};
      this.#buildAllocationsMap();
    }

    // Update one-time price
    if (this.refs.onetimePrice && variant.price) {
      this.refs.onetimePrice.textContent = this.#formatMoney(variant.price);
    }

    // Update subscription prices in dropdown
    this.#updateSellingPlanOptions(variant);

    // Update subscription price display
    if (this.refs.subscriptionPrice && this.refs.subscriptionRadio.checked) {
      const selectedOption = this.refs.sellingPlanSelect.selectedOptions[0];
      if (selectedOption) {
        const price = selectedOption.dataset.price;
        if (price) {
          this.refs.subscriptionPrice.textContent = this.#formatMoney(parseInt(price, 10));
        }
      }
    }

    // Update compare price in subscription option
    const comparePrice = this.refs.subscriptionOption?.querySelector('.subscription-widget__price-compare');
    if (comparePrice && variant.price) {
      comparePrice.textContent = this.#formatMoney(variant.price);
    }
  };

  /**
   * Updates selling plan dropdown options with new variant prices
   * @param {Object} variant - The new variant object
   */
  #updateSellingPlanOptions(variant) {
    if (!variant.selling_plan_allocations) return;

    const options = this.refs.sellingPlanSelect.options;

    for (const option of options) {
      const sellingPlanId = parseInt(option.value, 10);
      const allocation = variant.selling_plan_allocations.find(
        (a) => a.selling_plan_id === sellingPlanId
      );

      if (allocation) {
        option.dataset.price = allocation.price.toString();

        // Update displayed subscription price for selected option
        if (option.selected && this.refs.subscriptionPrice) {
          this.refs.subscriptionPrice.textContent = this.#formatMoney(allocation.price);
        }
      }
    }
  }
}

if (!customElements.get('subscription-widget')) {
  customElements.define('subscription-widget', SubscriptionWidget);
}
